import {
  Controller,
  useFieldArray,
  type Control,
  type FieldErrors,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import { Button, Input, Label, ListBox, Select } from '@heroui/react';
import {
  AI_BILLING_DIMENSIONS,
  AI_BILLING_DIMENSION_LABELS,
  AI_BILLING_UNITS,
  AI_BILLING_UNIT_LABELS,
  AI_DIMENSION_UNIT_MAP,
  type AiBillingDimension,
  type AiBillingUnit,
  type BillingRuleInputParameters,
} from '../constants';
import { BillingRuleInputParametersField } from './BillingRuleInputParametersField';

export type BillingRuleLineFormValues = {
  inputParameters?: BillingRuleInputParameters;
  usage: AiBillingDimension;
  unit: AiBillingUnit;
  costPrice: number;
  price: number;
};

export type BillingRuleFormWithLines = {
  rule: BillingRuleLineFormValues[];
};

const RULE_LINE_GRID =
  'grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,0.75fr)_minmax(0,0.75fr)_2rem] items-center gap-2';

const EMPTY_RULE_LINE: BillingRuleLineFormValues = {
  usage: 'INPUT_TOKEN',
  unit: '1M_TOKEN',
  costPrice: 0,
  price: 0,
};

interface AiBillingRuleLinesEditorProps<T extends BillingRuleFormWithLines> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  errors?: FieldErrors<T>;
}

export function AiBillingRuleLinesEditor<T extends BillingRuleFormWithLines>({
  control,
  setValue,
  watch,
  errors,
}: AiBillingRuleLinesEditorProps<T>) {
  const { fields, append, remove } = useFieldArray({ control, name: 'rule' as never });
  const ruleErrors = errors?.rule as FieldErrors<BillingRuleLineFormValues>[] | undefined;

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">计价规则</Label>
        <Button
          isIconOnly
          aria-label="添加计价项"
          size="sm"
          variant="ghost"
          onPress={() => append({ ...EMPTY_RULE_LINE } as never)}
        >
          <IconPlus className="size-4" stroke={1.5} />
        </Button>
      </div>

      <div className={`${RULE_LINE_GRID} px-0.5 text-xs text-muted-foreground`}>
        <span>参数规格</span>
        <span>计价维度</span>
        <span>计价单位</span>
        <span>成本价</span>
        <span>售价</span>
        <span />
      </div>

      {fields.length === 0 ? (
        <p className="text-xs text-muted-foreground">暂无计价项，点击右上角添加</p>
      ) : null}

      {fields.map((field, index) => {
        const usageName = `rule.${index}.usage` as const;
        const unitName = `rule.${index}.unit` as const;
        const currentUsage = watch(`rule.${index}.usage` as never) as unknown as
          | AiBillingDimension
          | undefined;
        const unitOptions = currentUsage ? AI_DIMENSION_UNIT_MAP[currentUsage] : AI_BILLING_UNITS;

        return (
          <div key={field.id} className={RULE_LINE_GRID}>
            <Controller
              control={control}
              name={`rule.${index}.inputParameters` as never}
              render={({ field: paramsField }) => (
                <BillingRuleInputParametersField
                  value={paramsField.value as BillingRuleInputParameters | undefined}
                  onChange={(next) => paramsField.onChange(next)}
                />
              )}
            />

            <Controller
              control={control}
              name={usageName as never}
              render={({ field: usageField }) => (
                <Select
                  aria-label="计价维度"
                  variant="secondary"
                  value={usageField.value as string}
                  onChange={(key) => {
                    if (key == null) return;
                    const usage = String(key) as AiBillingDimension;
                    usageField.onChange(usage);
                    const allowed = AI_DIMENSION_UNIT_MAP[usage];
                    const currentUnit = watch(unitName as never) as unknown as
                      | AiBillingUnit
                      | undefined;
                    if (!currentUnit || !allowed.includes(currentUnit)) {
                      setValue(unitName as never, allowed[0] as never);
                    }
                  }}
                >
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      {AI_BILLING_DIMENSIONS.map((d) => (
                        <ListBox.Item key={d} id={d} textValue={AI_BILLING_DIMENSION_LABELS[d]}>
                          {AI_BILLING_DIMENSION_LABELS[d]}
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              )}
            />

            <Controller
              control={control}
              name={unitName as never}
              render={({ field: unitField }) => (
                <Select
                  aria-label="计价单位"
                  variant="secondary"
                  value={unitField.value as string}
                  onChange={(key) => {
                    if (key != null) unitField.onChange(String(key));
                  }}
                >
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      {unitOptions.map((u) => (
                        <ListBox.Item key={u} id={u} textValue={AI_BILLING_UNIT_LABELS[u]}>
                          {AI_BILLING_UNIT_LABELS[u]}
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              )}
            />

            <Controller
              control={control}
              name={`rule.${index}.costPrice` as never}
              render={({ field: priceField }) => (
                <Input
                  aria-label="成本价"
                  type="number"
                  min={0}
                  step="any"
                  placeholder="请输入"
                  variant="secondary"
                  value={String(priceField.value ?? '')}
                  onChange={(e) => priceField.onChange(e.target.value)}
                  onBlur={priceField.onBlur}
                  name={priceField.name}
                  ref={priceField.ref}
                />
              )}
            />

            <Controller
              control={control}
              name={`rule.${index}.price` as never}
              render={({ field: priceField }) => (
                <Input
                  aria-label="售价"
                  type="number"
                  min={0}
                  step="any"
                  placeholder="请输入"
                  variant="secondary"
                  value={String(priceField.value ?? '')}
                  onChange={(e) => priceField.onChange(e.target.value)}
                  onBlur={priceField.onBlur}
                  name={priceField.name}
                  ref={priceField.ref}
                />
              )}
            />

            <Button
              isIconOnly
              aria-label="删除计价项"
              size="sm"
              variant="ghost"
              onPress={() => remove(index)}
            >
              <IconTrash className="size-4" stroke={1.5} />
            </Button>
          </div>
        );
      })}

      {errors?.rule && typeof errors.rule === 'object' && 'message' in errors.rule ? (
        <p className="text-xs text-danger">{String(errors.rule.message)}</p>
      ) : null}
    </div>
  );
}

export { EMPTY_RULE_LINE };
